import React, { useState, useEffect } from "react";
import axios from "axios";
import { getApiBase } from "./apiBase";
import "./settings.css";

const API_BASE = getApiBase();
const API = `${API_BASE}/api/settings`;

/** POST /api/settings/… — on 404 retry /settings/… (same Express router; helps nginx / old paths). */
async function postSettings(suffix, body) {
  const primary = `${API_BASE}/api/settings${suffix}`;
  const fallback = `${API_BASE}/settings${suffix}`;
  try {
    return await axios.post(primary, body);
  } catch (err) {
    if (err.response?.status === 404) {
      return await axios.post(fallback, body);
    }
    throw err;
  }
}

async function getSettings() {
  const fallback = `${API_BASE}/settings`;
  try {
    return await axios.get(API);
  } catch (err) {
    if (err.response?.status === 404) {
      return await axios.get(fallback);
    }
    throw err;
  }
}

function formatApiError(err) {
  const status = err.response?.status;
  const body = err.response?.data?.error;
  if (status === 404) {
    return "404 — route missing on the server. Stop and restart Node from the latest backend code (git pull). Dev: API must be on port 5000 or set REACT_APP_API_BASE_URL. Production: deploy backend + nginx without stripping /api (see deploy/nginx-socialwall.conf).";
  }
  if (body) return typeof body === "string" ? body : JSON.stringify(body);
  return err.message || "Request failed";
}

export default function Settings() {
  const [settings, setSettings] = useState({
    has_token: false,
    token_valid: null,
    access_token_hint: "",
    hashtag_id: "",
    hashtag_name: "",
    user_id: "",
    facebook_app_id: "",
    instagram_fetch_enabled: true,
    has_meta_app_credentials: false,
  });
  const [token, setToken] = useState("");
  const [shortLivedToken, setShortLivedToken] = useState("");
  const [metaClientId, setMetaClientId] = useState("");
  const [metaClientSecret, setMetaClientSecret] = useState("");
  const [igUserId, setIgUserId] = useState("");
  const [hashtag, setHashtag] = useState("");
  const [tokenStatus, setTokenStatus] = useState(null);
  const [hashtagStatus, setHashtagStatus] = useState(null);
  const [fetchMsg, setFetchMsg] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [hashtagLoading, setHashtagLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [exchangeLoading, setExchangeLoading] = useState(false);
  const [exchangeMsg, setExchangeMsg] = useState(null);
  const [metaAppLoading, setMetaAppLoading] = useState(false);
  const [metaAppMsg, setMetaAppMsg] = useState(null);
  const [igUserLoading, setIgUserLoading] = useState(false);
  const [igUserMsg, setIgUserMsg] = useState(null);

  const applySettingsPayload = (data) => {
    setSettings((prev) => ({
      ...prev,
      ...data,
      instagram_fetch_enabled: data.instagram_fetch_enabled !== false,
    }));
    if (data.facebook_app_id != null) setMetaClientId(String(data.facebook_app_id));
    if (data.user_id != null) setIgUserId(String(data.user_id));
    if (data.hashtag_name != null && String(data.hashtag_name).trim() !== "") {
      setHashtag(`#${String(data.hashtag_name).replace(/^#/, "")}`);
    }
  };

  useEffect(() => {
    getSettings()
      .then((res) => {
        applySettingsPayload(res.data);
      })
      .catch(() => {});
  }, []);

  // Pre-fill hashtag input when API sends hashtag_name (incl. server backfill from hashtag id).
  useEffect(() => {
    if (!settings.hashtag_name || !String(settings.hashtag_name).trim()) return;
    const formatted = `#${String(settings.hashtag_name).replace(/^#/, "")}`;
    setHashtag((prev) => (prev === "" ? formatted : prev));
  }, [settings.hashtag_name]);

  const importRunning = settings.instagram_fetch_enabled !== false;

  const toggleInstagramFetch = async () => {
    const nextEnabled = !importRunning;
    setFetchLoading(true);
    setFetchMsg(null);
    try {
      const res = await postSettings("/instagram-fetch", { enabled: nextEnabled });
      setSettings((prev) => ({ ...prev, instagram_fetch_enabled: res.data.instagram_fetch_enabled }));
      setFetchMsg({ type: "success", msg: res.data.message });
    } catch (err) {
      const isNetwork = !err.response && err.message === "Network Error";
      setFetchMsg({
        type: "error",
        msg: isNetwork
          ? "❌ Network error — can’t reach the API. Production: open /api/health. Dev: backend should run on port 5000 (or set REACT_APP_API_BASE_URL) and restart npm start."
          : `❌ ${formatApiError(err)}`,
      });
    }
    setFetchLoading(false);
  };

  const exchangeLongLived = async () => {
    if (!shortLivedToken.trim()) {
      return setExchangeMsg({ type: "error", msg: "Paste a short-lived access token from Graph API Explorer." });
    }
    if (!settings.has_meta_app_credentials && (!metaClientId.trim() || !metaClientSecret.trim())) {
      return setExchangeMsg({
        type: "error",
        msg: "Add FACEBOOK_APP_ID and FACEBOOK_APP_SECRET to server .env, or fill App ID and App Secret here.",
      });
    }
    setExchangeLoading(true);
    setExchangeMsg(null);
    try {
      const body = { short_lived_token: shortLivedToken.trim() };
      if (!settings.has_meta_app_credentials) {
        body.client_id = metaClientId.trim();
        body.client_secret = metaClientSecret.trim();
      }
      const res = await postSettings("/exchange-long-lived", body);
      setExchangeMsg({ type: "success", msg: `✅ ${res.data.message}` });
      setShortLivedToken("");
      setMetaClientSecret("");
      const updated = await getSettings();
      applySettingsPayload(updated.data);
    } catch (err) {
      setExchangeMsg({
        type: "error",
        msg: `❌ ${formatApiError(err)}`,
      });
    }
    setExchangeLoading(false);
  };

  const saveMetaApp = async () => {
    if (!metaClientId.trim()) {
      return setMetaAppMsg({ type: "error", msg: "App ID is required." });
    }
    setMetaAppLoading(true);
    setMetaAppMsg(null);
    try {
      const res = await postSettings("/meta-app", {
        client_id: metaClientId.trim(),
        client_secret: metaClientSecret.trim() || undefined,
      });
      setMetaClientSecret("");
      setMetaAppMsg({ type: "success", msg: `✅ ${res.data.message}` });
      const fresh = await getSettings();
      applySettingsPayload(fresh.data);
    } catch (err) {
      setMetaAppMsg({ type: "error", msg: `❌ ${formatApiError(err)}` });
    }
    setMetaAppLoading(false);
  };

  const saveIgUserId = async () => {
    if (!igUserId.trim()) {
      return setIgUserMsg({ type: "error", msg: "Instagram User ID is required." });
    }
    setIgUserLoading(true);
    setIgUserMsg(null);
    try {
      const res = await postSettings("/instagram-user-id", { user_id: igUserId.trim() });
      applySettingsPayload({ ...res.data, user_id: res.data.user_id });
      setIgUserMsg({ type: "success", msg: `✅ ${res.data.message}` });
      const fresh = await getSettings();
      applySettingsPayload(fresh.data);
    } catch (err) {
      setIgUserMsg({ type: "error", msg: `❌ ${formatApiError(err)}` });
    }
    setIgUserLoading(false);
  };

  const saveToken = async () => {
    if (!token.trim()) {
      if (settings.has_token) {
        return setTokenStatus({
          type: "error",
          msg: "Paste a new token to replace the saved one, or leave this field empty.",
        });
      }
      return setTokenStatus({ type: "error", msg: "Please enter an access token." });
    }
    setTokenLoading(true);
    setTokenStatus(null);
    try {
      await postSettings("/token", { access_token: token.trim() });
      setTokenStatus({ type: "success", msg: "✅ Token saved! Wall will use the new token on next fetch." });
      setToken("");
      const res = await getSettings();
      applySettingsPayload(res.data);
    } catch (err) {
      setTokenStatus({ type: "error", msg: `❌ ${formatApiError(err)}` });
    }
    setTokenLoading(false);
  };

  const tokenStatusIcon = () => {
    if (!settings.has_token) return "🔴";
    if (settings.token_valid === true) return "🟢";
    if (settings.token_valid === false) return "🔴";
    return "🟡";
  };

  const tokenCardInvalid = settings.has_token && settings.token_valid === false;

  const saveHashtag = async () => {
    if (!hashtag.trim()) return setHashtagStatus({ type: "error", msg: "Please enter a hashtag." });
    setHashtagLoading(true);
    setHashtagStatus(null);
    try {
      const res = await postSettings("/hashtag", { hashtag: hashtag.trim() });
      setHashtagStatus({ type: "success", msg: `✅ ${res.data.message}` });
      setHashtag("");
      const updated = await getSettings();
      applySettingsPayload(updated.data);
    } catch (err) {
      setHashtagStatus({ type: "error", msg: `❌ ${formatApiError(err)}` });
    }
    setHashtagLoading(false);
  };

  const tokenPlaceholder =
    settings.has_token && settings.access_token_hint
      ? `Current saved token ${settings.access_token_hint} — paste a new full token here to replace`
      : "Paste your Instagram long-lived access token here…";

  return (
    <div className="settings-page">
      <div className="settings-bg"></div>
      <div className="settings-container">
        <div className="settings-header">
          <div className="settings-logo">⚙️</div>
          <h1 className="settings-title">Social Wall Settings</h1>
          <p className="settings-subtitle">Configure your Instagram integration</p>
        </div>

        {/* Current Config Status */}
        <div className="status-cards">
          <div className={`status-card${tokenCardInvalid ? " status-card--token-invalid" : ""}`}>
            <div className="status-icon">{tokenStatusIcon()}</div>
            <div>
              <div className="status-label">Access Token</div>
              <div className="status-value">
                {!settings.has_token
                  ? "Not configured"
                  : settings.token_valid === false
                    ? "Token expired or invalid — paste a new token below"
                    : settings.token_valid === null
                      ? `${settings.access_token_hint} (could not verify; check network)`
                      : settings.access_token_hint}
              </div>
            </div>
          </div>
          <div className="status-card">
            <div className="status-icon">🏷️</div>
            <div>
              <div className="status-label">Hashtag ID</div>
              <div className="status-value">{settings.hashtag_id || "Not configured"}</div>
            </div>
          </div>
          <div className={`status-card${importRunning ? "" : " status-card--paused"}`}>
            <div className="status-icon">{importRunning ? "🟢" : "⏸️"}</div>
            <div>
              <div className="status-label">Instagram import</div>
              <div className="status-value">{importRunning ? "Running — Graph API on schedule" : "Paused — wall uses DB only"}</div>
            </div>
          </div>
        </div>

        {/* Pause / resume Graph API import */}
        <div className="settings-card settings-card--fetch">
          <div className="card-head">
            <span className="card-icon">{importRunning ? "⏸️" : "▶️"}</span>
            <div>
              <h2>Instagram import</h2>
              <p>Pause to stop Meta API calls (e.g. rate limits). The wall still shows posts already saved.</p>
            </div>
          </div>
          <div className="fetch-toggle-row">
            <button
              type="button"
              className={`settings-btn settings-btn--fetch ${importRunning ? "settings-btn--pause" : "settings-btn--resume"}`}
              onClick={toggleInstagramFetch}
              disabled={fetchLoading}
            >
              {fetchLoading ? <span className="spinner"></span> : importRunning ? "Pause import" : "Resume import"}
            </button>
          </div>
          {fetchMsg && <div className={`status-msg ${fetchMsg.type}`}>{fetchMsg.msg}</div>}
        </div>

        {/* Exchange short-lived → long-lived (~60 days) */}
        <div className="settings-card settings-card--fetch">
          <div className="card-head">
            <span className="card-icon">🔄</span>
            <div>
              <h2>Get long-lived token (~60 days)</h2>
              <p>
                In{" "}
                <a
                  href="https://developers.facebook.com/tools/explorer/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="settings-inline-link"
                >
                  Graph API Explorer
                </a>
                , generate a <strong>short-lived</strong> user token with the permissions your app needs, then exchange it here. Values below load from your server <code className="settings-code">.env</code>; edit and save.
              </p>
            </div>
          </div>
          <div className="exchange-meta-fields">
            <input
              className="settings-input"
              type="text"
              autoComplete="off"
              placeholder="Facebook App ID"
              value={metaClientId}
              onChange={(e) => setMetaClientId(e.target.value)}
            />
            <input
              className="settings-input"
              type="password"
              autoComplete="new-password"
              placeholder={
                settings.has_meta_app_credentials
                  ? "App Secret (optional — type new secret to replace stored one)"
                  : "Facebook App Secret"
              }
              value={metaClientSecret}
              onChange={(e) => setMetaClientSecret(e.target.value)}
            />
          </div>
          <div className="fetch-toggle-row settings-btn-row">
            <button
              type="button"
              className="settings-btn settings-btn--muted settings-btn--fetch"
              onClick={saveMetaApp}
              disabled={metaAppLoading}
            >
              {metaAppLoading ? <span className="spinner"></span> : "Save App ID & Secret"}
            </button>
          </div>
          {metaAppMsg && <div className={`status-msg ${metaAppMsg.type}`}>{metaAppMsg.msg}</div>}
          <textarea
            className="settings-input token-input"
            style={{ marginTop: 16 }}
            placeholder="Short-lived access token (from Graph API Explorer) — paste only for exchange, not saved as display"
            value={shortLivedToken}
            onChange={(e) => setShortLivedToken(e.target.value)}
            rows={3}
          />
          <div className="fetch-toggle-row">
            <button
              type="button"
              className="settings-btn settings-btn--resume settings-btn--fetch"
              onClick={exchangeLongLived}
              disabled={exchangeLoading}
            >
              {exchangeLoading ? <span className="spinner"></span> : "Exchange & save long-lived token"}
            </button>
          </div>
          {exchangeMsg && <div className={`status-msg ${exchangeMsg.type}`}>{exchangeMsg.msg}</div>}
        </div>

        {/* Token Section */}
        <div className={`settings-card${tokenCardInvalid ? " settings-card--token-warning" : ""}`}>
          <div className="card-head">
            <span className="card-icon">🔑</span>
            <div>
              <h2>Instagram Access Token</h2>
              <p>
                Full tokens are not shown for security — only a short hint in the status card above. Paste a new token
                below to replace the saved one.
              </p>
            </div>
          </div>
          <div className="input-group">
            <textarea
              className="settings-input token-input"
              placeholder={tokenPlaceholder}
              value={token}
              onChange={(e) => setToken(e.target.value)}
              rows={3}
            />
            <button
              className={`settings-btn ${tokenLoading ? "loading" : ""}`}
              onClick={saveToken}
              disabled={tokenLoading}
            >
              {tokenLoading ? <span className="spinner"></span> : "Save Token"}
            </button>
          </div>
          {tokenStatus && <div className={`status-msg ${tokenStatus.type}`}>{tokenStatus.msg}</div>}
        </div>

        {/* Instagram User ID */}
        <div className="settings-card">
          <div className="card-head">
            <span className="card-icon">👤</span>
            <div>
              <h2>Instagram User ID</h2>
              <p>Your Instagram Business/Creator scoped user id (used for hashtag search). Loaded from server — edit and save.</p>
            </div>
          </div>
          <div className="input-group">
            <input
              className="settings-input"
              type="text"
              autoComplete="off"
              placeholder="e.g. 178414…"
              value={igUserId}
              onChange={(e) => setIgUserId(e.target.value)}
            />
            <button
              type="button"
              className={`settings-btn ${igUserLoading ? "loading" : ""}`}
              onClick={saveIgUserId}
              disabled={igUserLoading}
            >
              {igUserLoading ? <span className="spinner"></span> : "Save User ID"}
            </button>
          </div>
          {igUserMsg && <div className={`status-msg ${igUserMsg.type}`}>{igUserMsg.msg}</div>}
        </div>

        {/* Hashtag Section */}
        <div className="settings-card">
          <div className="card-head">
            <span className="card-icon">#</span>
            <div>
              <h2>Instagram Hashtag</h2>
              <p>Hashtag text is saved so you can edit it later. ID is resolved automatically.</p>
            </div>
          </div>
          <div className="input-group">
            <input
              className="settings-input"
              type="text"
              placeholder="#yourhashtag or yourhashtag"
              value={hashtag}
              onChange={(e) => setHashtag(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && saveHashtag()}
            />
            <button
              className={`settings-btn ${hashtagLoading ? "loading" : ""}`}
              onClick={saveHashtag}
              disabled={hashtagLoading}
            >
              {hashtagLoading ? <span className="spinner"></span> : "Update Hashtag"}
            </button>
          </div>
          {hashtagStatus && <div className={`status-msg ${hashtagStatus.type}`}>{hashtagStatus.msg}</div>}
        </div>

        <div className="settings-footer">
          <a href="/" className="back-link">← Back to Wall</a>
        </div>
      </div>
    </div>
  );
}
